import type { User } from '@/core/entities/user';
import { attemptTrack } from '@/core/service/mixpanel/utils';
import type { Config, Dict, Mixpanel } from 'mixpanel-browser';
import mixpanel from 'mixpanel-browser';
import { v4 as uuidv4 } from 'uuid';

interface MixpanelAsyncEvent {
	eventData: Dict;
	timerTrack: null | ReturnType<typeof setTimeout>;
	timerDelete: null | ReturnType<typeof setTimeout>;
}

export interface MixpanelService {
	init: () => void;
	config: (user: User | null) => void;
	register: (props: Dict, days?: number) => void;
	track: (eventName: string, properties?: Dict) => void;
	mixpanelEvents: Map<string, MixpanelAsyncEvent>;
	asyncTrack: (
		eventName: string,
		eventProperties: MixpanelAsyncEvent['eventData'],
		timerTrack?: number,
		timerDelete?: number
	) => void;
	asyncTrigger: (eventName: string) => void;
	isInitialized: () => boolean;
	getAnonymousId: () => string | null;
	setAnonymousId: (id: string) => void;
	aliasUser: (userId: string, anonymousId?: string) => void;
}

const ANONYMOUS_ID_COOKIE = 'pilar_anon_id';

function generateUUID(): string {
	return uuidv4();
}

function getOrCreateAnonymousId(): string {
	const cookies = useCookie(ANONYMOUS_ID_COOKIE);

	if (!cookies.value) {
		const newId = generateUUID();
		cookies.value = newId;
		return newId;
	}

	return cookies.value;
}

export function mixpanelService(
	token: string,
	config: Partial<Config>,
	name: string,
	version: string
): MixpanelService {
	const mixpanelInstance = ref<Mixpanel | null>(null);
	const anonymousId = shallowRef<string | null>(null);

	return {
		init() {
			if (this.isInitialized()) return;

			mixpanelInstance.value = mixpanel.init(token, config, name);

			if (!anonymousId.value) {
				anonymousId.value = getOrCreateAnonymousId();

				if (mixpanelInstance.value) {
					mixpanelInstance.value.register({
						$anon_id: anonymousId.value,
						version: version
					});
				}
			}
		},
		config(user) {
			if (!user || !user.email || !mixpanelInstance.value) return;

			if (anonymousId.value) {
				this.aliasUser(user.email, anonymousId.value);
			}

			mixpanelInstance.value.identify(user.email);
			mixpanelInstance.value.people.set({
				ID: user.id,
				email: user.email,
				name: user.name,
				hasCompletedRegistration: user.hasCompletedRegistration,
				$anon_id: anonymousId.value,
				version: version
			});
		},
		async register(props: Dict, days?: number) {
			if (mixpanelInstance.value) {
				mixpanelInstance.value.register(props, days);
			}
		},
		async track(eventName: string, properties?: Dict) {
			if (mixpanelInstance.value) {
				const enrichedProps = {
					...(properties || {}),
					$anon_id: anonymousId.value
				};
				attemptTrack(mixpanelInstance.value, eventName, enrichedProps);
			}
		},
		mixpanelEvents: new Map(),
		asyncTrack(
			eventName: string,
			eventProperties: Dict,
			timerTrack?: number,
			timerDelete?: number
		) {
			const enrichedProps = {
				...eventProperties,
				$anon_id: anonymousId.value
			};

			this.mixpanelEvents.set(eventName, {
				eventData: {
					...(this.mixpanelEvents.get(eventName)?.eventData || {}),
					...enrichedProps
				},
				timerTrack: timerTrack
					? setTimeout(() => {
							this.track(eventName, enrichedProps);
							this.mixpanelEvents.delete(eventName);
						})
					: null,
				timerDelete: timerDelete
					? setTimeout(() => {
							this.mixpanelEvents.delete(eventName);
						})
					: null
			});
		},
		asyncTrigger(eventName: string) {
			let eventProperties = this.mixpanelEvents.get(eventName)?.eventData;

			if (!eventProperties) return;

			const track = this.track(eventName, eventProperties);
			this.mixpanelEvents.delete(eventName);
			return track;
		},
		isInitialized() {
			return mixpanelInstance.value !== null;
		},
		getAnonymousId() {
			return anonymousId.value;
		},
		setAnonymousId(id: string) {
			anonymousId.value = id;
			const cookies = useCookie(ANONYMOUS_ID_COOKIE);
			cookies.value = id;

			if (mixpanelInstance.value) {
				mixpanelInstance.value.register({
					$anon_id: id
				});
			}
		},
		aliasUser(userId: string, anonId?: string) {
			if (!mixpanelInstance.value) return;

			const idToAlias = anonId || anonymousId.value;
			if (idToAlias) {
				mixpanelInstance.value.alias(userId, idToAlias);
			}
		}
	};
}
